import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useAlerts } from '@/hooks/useAlerts';
import { useClients } from '@/hooks/useClients';
import { useSites } from '@/hooks/useSites';
import { useAuth } from '@/contexts/AuthContext';
import { useMentions } from '@/hooks/useMentions';
import { logActivity } from '@/hooks/useActivityLog';
import { AlertTriangle, AlertCircle, Info, Plus, Check } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { MentionTextarea } from '@/components/MentionTextarea';

const alertTypeIcons = {
  critical: AlertTriangle,
  warning: AlertCircle,
  info: Info
};

const alertTypeColors = {
  critical: 'destructive',
  warning: 'warning',
  info: 'secondary'
} as const;

export function EnhancedAlertsSection() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { alerts, isLoading, createAlert, updateAlert } = useAlerts();
  const { clients } = useClients();
  const { sites } = useSites();
  const { createMentions } = useMentions();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<'critical' | 'warning' | 'info'>('warning');
  const [category, setCategory] = useState<'staff' | 'service' | 'building' | 'system'>('system');
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [selectedSite, setSelectedSite] = useState<string>('');

  const handleCreate = async () => {
    if (!title.trim() || !description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    const alertData: any = {
      title,
      description,
      type,
      category
    };

    if (selectedClient) alertData.clientId = selectedClient;
    if (selectedSite) alertData.siteId = selectedSite;

    // Create alert first
    createAlert(alertData);

    await logActivity({
      actionType: 'create_alert',
      actionDetails: {
        alert_type: type,
        category,
        client_id: selectedClient || null
      },
      siteId: selectedSite || undefined
    });

    setIsCreateOpen(false);
    setTitle('');
    setDescription('');
    setType('warning');
    setCategory('system');
    setSelectedClient('');
    setSelectedSite('');
  };

  const handleResolve = async (alertId: string) => {
    // Find the alert to check if it's a mail alert
    const alert = alerts.find(a => a.id === alertId);
    const isMailAlert = alert && ['has mail', 'has package', 'has mail & packages'].some(p => alert.title.toLowerCase().includes(p));

    // Transform title for mail alerts
    if (isMailAlert && alert) {
      const newTitle = alert.title
        .replace('has mail & packages', 'received mail & packages')
        .replace('has mail', 'received mail')
        .replace('has package', 'received package');
      
      const { data: { user } } = await supabase.auth.getUser();
      await supabase.from('alerts').update({
        status: 'resolved',
        title: newTitle,
        acknowledged_by: user?.id
      }).eq('id', alertId);
      
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    } else {
      updateAlert({
        id: alertId,
        status: 'resolved'
      });
    }

    await logActivity({
      actionType: 'acknowledge_alert',
      actionDetails: { 
        alert_id: alertId,
        alertTitle: alert?.title || '',
        clientId: alert?.clientId || null,
        action: 'resolved',
        timestamp: new Date().toISOString()
      }
    });
  };

  const activeAlerts = alerts.filter(a => a.status === 'active');
  const groupedAlerts = sites.map(site => ({
    site,
    alerts: activeAlerts.filter((a: any) => a.site_id === site.id)
  })).filter(g => g.alerts.length > 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold">Alerts & Notifications</h2>
          <p className="text-muted-foreground">Monitor and respond to facility alerts</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Create Alert
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Alert</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input 
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Alert title"
                />
              </div>

              <div>
                <Label>Description * (Use @ to mention staff or clients)</Label>
                <MentionTextarea
                  value={description}
                  onChange={setDescription}
                  placeholder="Describe the alert... Type @ to mention staff or clients"
                  className="min-h-[100px]"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Severity</Label>
                  <Select value={type} onValueChange={(v: any) => setType(v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="critical">Critical</SelectItem>
                      <SelectItem value="warning">Warning</SelectItem>
                      <SelectItem value="info">Info</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Category</Label>
                  <Select value={category} onValueChange={(v: any) => setCategory(v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="staff">​</SelectItem>
                      <SelectItem value="service">Service</SelectItem>
                      <SelectItem value="building">Building</SelectItem>
                      <SelectItem value="system">System</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Related Client (Optional)</Label>
                <Select value={selectedClient || "none"} onValueChange={(v) => setSelectedClient(v === "none" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {clients.map((client: any) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.first_name} {client.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Facility (Optional)</Label>
                <Select value={selectedSite || "all"} onValueChange={(v) => setSelectedSite(v === "all" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select facility" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Facilities</SelectItem>
                    {sites.map(site => (
                      <SelectItem key={site.id} value={site.id}>
                        {site.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button onClick={handleCreate} className="w-full">
                Create Alert
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4">
        {activeAlerts.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Info className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No active alerts</h3>
              <p className="text-muted-foreground">All clear! No alerts at this time.</p>
            </CardContent>
          </Card>
        ) : (
          <Accordion type="single" collapsible className="space-y-4">
            {groupedAlerts.map(({ site, alerts: siteAlerts }) => (
              <AccordionItem key={site.id} value={site.id} className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center justify-between w-full pr-4">
                    <span className="font-semibold">{site.name}</span>
                    <Badge variant="secondary">{siteAlerts.length} active</Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 pt-4">
                    {siteAlerts.map((alert: any) => {
                      const Icon = alertTypeIcons[alert.type as keyof typeof alertTypeIcons];
                      const client: any = alert.clientId ? clients.find(c => c.id === alert.clientId) : null;
                      
                      return (
                        <Card key={alert.id}>
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <Icon className="h-5 w-5" />
                                  <h4 className="font-semibold">{alert.title}</h4>
                                  <Badge variant={alert.type === 'critical' ? 'destructive' : 'secondary'}>
                                    {alert.type}
                                  </Badge>
                                  {alert.category && (
                                    <Badge variant="outline" className="capitalize">
                                      {alert.category}
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground mb-2">
                                  {alert.description}
                                </p>
                                {client && (client.first_name || client.last_name) && (
                                  <p className="text-sm">
                                    <span className="font-medium">Client:</span> {client.first_name} {client.last_name}
                                  </p>
                                )}
                                <p className="text-xs text-muted-foreground mt-2">
                                  {format(new Date(alert.timestamp), 'MMM d, yyyy h:mm a')}
                                </p>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="default"
                                  onClick={() => handleResolve(alert.id)}
                                >
                                  <Check className="h-4 w-4 mr-1" />
                                  Resolve
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </div>
    </div>
  );
}
